import BodyText from 'LuxKit/Typography/BodyText'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import type { MouseEvent } from 'react'
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Clickable from 'components/Common/Clickable/Clickable'
import clsx from 'clsx'
import type { BundledOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { nonNullable } from 'lib/array/arrayUtils'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import OfferCalendarDurationSelect from 'components/OfferPage/Filters/CalendarPicker/CalendarDuration/OfferCalendarDurationSelect'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Image from 'components/Common/Image'
import moment from 'moment'

const Container = styled.div`
  padding: ${rem(12)} ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  width: 100%;

  ${mediaQueryUp.tablet} {
    width: ${rem(300)};
  }

  &.bundle-and-save-v2 {
    padding: ${rem(16)};

    &.active {
      border: 1px solid ${props => props.theme.palette.neutral.default.one};
    }

    ${mediaQueryUp.tablet} {
      padding: ${rem(8)};
      width: auto;
    }
  }
`

const ClickableSelect = styled(Clickable)`
  --border-colour: ${props => props.theme.palette.neutral.default.five};
  --bg-colour: ${props => props.theme.palette.neutral.default.eight};

  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: ${rem(8)};
  align-items: center;
  padding: ${rem(8)};
  border: 1px solid var(--border-colour);
  border-radius: ${props => props.theme.borderRadius.M};
  background-color: var(--bg-colour);

  &.selected {
    --border-colour: ${props => props.theme.palette.neutral.default.one};
  }

  &.error {
    --border-colour: ${props => props.theme.palette.messaging.critical.normalForeground};
    --bg-colour: ${props => props.theme.palette.messaging.critical.lightForeground};
  }
`

const OfferImage = styled(Image)`
    display: block;
    width: ${rem(70)};
    height: ${rem(70)};
    object-fit: cover;

    ${mediaQueryUp.tablet} {
      width: ${rem(100)};
      height: ${rem(100)};
    }
 `

const DateSelectionChip = styled(AssistChip)`
  &.selected {
    background-color: ${props => props.theme.palette.neutral.default.one};
    border-color: ${props => props.theme.palette.neutral.default.one};
    color: ${props => props.theme.palette.neutral.default.eight};
  }
`

interface Props {
  hotelNumber: number
  bundleOffer: BundledOfferState
  onClick: (event: MouseEvent<HTMLButtonElement>, offerId?: string) => void
  onClickLabel: string
  isSelected: boolean
  isDatePickerOpen: boolean
  noSelections: boolean
}

const BundleAndSaveDateSelectionTrigger = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const intl = useIntl()
  const {
    hotelNumber,
    bundleOffer,
    onClick,
    onClickLabel,
    isSelected,
    isDatePickerOpen,
    noSelections,
  } = props
  const {
    checkInDate,
    checkOutDate,
    duration,
    offer,
  } = bundleOffer

  const [isDirty, setDirty] = useState(false)

  useEffect(() => {
    if (isDatePickerOpen) {
      setDirty(false)
    }
  }, [isDatePickerOpen])

  useEffect(() => {
    if (!bundleOffer.checkInDate && !isDatePickerOpen && !noSelections) {
      setDirty(true)
    }
  }, [bundleOffer, isDatePickerOpen, noSelections])

  const dateLabel = useMemo(() => {
    if (checkInDate) {
      const checkInMoment = moment(checkInDate)
      if (duration) {
        if (isBundleAndSaveV2Enabled()) {
          const checkOutDateDuration = checkInMoment.clone().add(duration, 'days')

          if (checkInMoment.month() === checkOutDateDuration.month()) {
            return `${checkInMoment.format('D')} - ${checkOutDateDuration.format('D MMM YYYY')}`
          }

          if (checkInMoment.year() !== checkOutDateDuration.year()) {
            return `${checkInMoment.format('DD MMM YYYY')} - ${checkOutDateDuration.format('DD MMM YYYY')}`
          }

          return `${checkInMoment.format('DD MMM')} - ${checkOutDateDuration.format('DD MMM YYYY')}`
        }

        return `${checkInMoment.format('DD MMM')} - ${checkInMoment.clone().add(duration, 'days').format(
          'DD MMM',
        )} (${duration} nights)`
      }

      if (checkOutDate) {
        const checkOutMoment = moment(checkOutDate)
        return `${checkInMoment.format('DD MMM')} - ${checkOutMoment.format(
          'DD MMM',
        )} (${checkOutMoment.diff(checkInDate, 'days')} nights)`
      }
    }

    return null
  }, [checkInDate, checkOutDate, duration])

  const handleClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    onClick(e, offer?.id)
  }, [onClick, offer])

  const locationLabel = useMemo(() => {
    return nonNullable([
      offer?.locationHeading,
      offer?.locationSubheading,
    ]).join(', ')
  }, [offer])

  return (
    <Container
      ref={ref}
      className={
        clsx({
          'bundle-and-save-v2': isBundleAndSaveV2Enabled(),
          active: isSelected && isDatePickerOpen,
        })
      }
    >
      {!isBundleAndSaveV2Enabled() && <>
        <Group direction="vertical">
          <BodyText variant="small">{intl.formatMessage(
            {
              id: 'lrVDVIGzIGbGyU8Da7GP',
              defaultMessage: 'Hotel {hotelNumber, number}',
              description: 'Label for hotel number in bundle and save date selection',
            },
            { hotelNumber },
          )}
          </BodyText>
          <BodyText lineClamp={1} variant="medium" weight="bold">
            {offer?.property?.name}
          </BodyText>
        </Group>
        <ClickableSelect
          onClick={handleClick}
          className={clsx({
            selected: isSelected && isDatePickerOpen,
            error: isDirty,
          })}
        >
          <LineCalendarIcon />
          <BodyText variant="medium">{dateLabel ?? onClickLabel}</BodyText>
          <DropdownChevron open={isSelected && isDatePickerOpen} />
        </ClickableSelect>
      </>}
      {isBundleAndSaveV2Enabled() &&
        <Group direction="horizontal" desktopVerticalAlign="center" gap={12}>
          <CSSBreakpoint min="desktop">
            <OfferImage id={offer?.images[0]?.id} />
          </CSSBreakpoint>
          <Group direction="vertical" gap={8} fullWidth>
            <Group
              direction="horizontal-reverse"
              tabletDirection="horizontal"
              gap={8}
              horizontalAlign="space-between"
              verticalAlign="center"
            >
              <CSSBreakpoint max="tablet">
                <OfferImage id={offer?.images[0]?.id} />
              </CSSBreakpoint>

              <Group direction="vertical">
                <BodyText variant="small">{locationLabel}</BodyText>
                <BodyText lineClamp={1} variant="large" weight="bold">
                  {offer?.property?.name}
                </BodyText>
              </Group>
            </Group>
            <Group direction="horizontal" gap={8}>
              <OfferCalendarDurationSelect isSelected={isSelected} offerId={offer?.id} />
              <DateSelectionChip
                variant="filled"
                size="medium"
                className={
                  clsx({
                    selected: isSelected && isDatePickerOpen,
                    error: isDirty,
                  })
                }
                endIcon={<DropdownChevron open={isSelected && isDatePickerOpen} />}
                onClick={handleClick}
              >
                {dateLabel ?? onClickLabel}
              </DateSelectionChip>
            </Group>
          </Group>
        </Group>}
    </Container>
  )
})

export default BundleAndSaveDateSelectionTrigger
